import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../root/root-types';
import { ScreenNameEnums, UserPreferenceDetail } from './model';
import { userViewPreferenceApi } from './api';

const USER_VIEW_PREFERENCE_FEATURE_KEY = 'userViewPreference';

type UserViewPreferenceState = Record<ScreenNameEnums, UserPreferenceDetail>;

export const initialUserViewPreferenceState: UserViewPreferenceState =
  Object.fromEntries(
    Object.values(ScreenNameEnums).map((x) => [x, {}])
  ) as UserViewPreferenceState;

export const userViewPreferenceSlice = createSlice({
  name: USER_VIEW_PREFERENCE_FEATURE_KEY,
  initialState: initialUserViewPreferenceState,
  reducers: {
    updateUserViewPreferenceByScreenName(
      state,
      action: PayloadAction<{
        screenName: ScreenNameEnums;
        data: UserPreferenceDetail;
      }>
    ) {
      const { screenName, data } = action.payload;
      state[screenName] = { ...state[screenName], ...data };
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      userViewPreferenceApi.endpoints.getUserViewPreference.matchFulfilled,
      (state, action) => {
        const data = action.payload;
        if (data?.screenName) {
          state[data.screenName] = data.preference;
        }
      }
    );
    builder.addMatcher(
      userViewPreferenceApi.endpoints.updateUserViewPreference.matchPending,
      (state, action) => {
        const data = action.meta.arg.originalArgs;
        data.preference.forEach((x) => {
          const { screenName, ...rest } = x;
          state[screenName] = { ...rest };
        });
      }
    );
  },
});

export const userViewPreferenceReducer = userViewPreferenceSlice.reducer;

export const { updateUserViewPreferenceByScreenName } =
  userViewPreferenceSlice.actions;

const getUserPreferenceState = (
  rootState: RootState
): UserViewPreferenceState => rootState[USER_VIEW_PREFERENCE_FEATURE_KEY];

export const getUserViewPreferenceState = createSelector(
  getUserPreferenceState,
  (state) => state
);

export const getUserViewPreferenceByScreenName = (screen: ScreenNameEnums) => {
  return createSelector(getUserPreferenceState, (state) => state[screen]);
};
